import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiSearch, FiChevronDown, FiEdit, FiTrash2, FiEye, FiRefreshCw, FiFileText, FiPlus, FiX, FiDownload } from "react-icons/fi";
import { approvalService, contentService, draftsService, revisionService } from "../../services/firestoreService";
import { auth } from "../../firebase";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import ContentNewPanel from "./ContentNewPanel.jsx";
import { createPDF } from "../../utils/pdfExport";
import * as XLSX from 'xlsx';
import { useLanguage } from "../../contexts/LanguageContext";
import Portal from "../../components/Portal";
import "./staffcss/contentlibrary.css";
import "../AdminPage.css";

const CONTENT_CATEGORIES = [
  "Contraception",
  "Fertility Basics",
  "STI Awareness",
  "Mental Health in Reproductive Care",
  "Male Reproductive Health",
  "Pregnancy Prevention",
  "General Reproductive Wellness"
];

// ---------------- Export Modal ----------------
function ExportModal({ isOpen, onClose, onExport, isExporting, activeTab }) {
  const [selectedFormat, setSelectedFormat] = useState(null);

  useEffect(() => {
    const scrollContainer = document.querySelector('.mp-workspace');
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousContainerOverflow = scrollContainer?.style.overflow ?? '';

    document.body.style.overflow = 'hidden';
    if (scrollContainer) {
      scrollContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (scrollContainer) {
        scrollContainer.style.overflow = previousContainerOverflow;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      setSelectedFormat(null);
    }
  }, [isOpen]);

  const handleExport = (format) => {
    setSelectedFormat(format);
    onExport(format);
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay export-modal-overlay">
        <div className="modal-content export-modal-content">
          <div className="modal-header">
            <h3 className="modal-title-white">Export Report</h3>
            <button className="modal-close" onClick={onClose}>
              <FiX />
            </button>
          </div>

          <div className="export-modal-body">
            <p className="export-modal-subtitle">Select export format for {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} tab</p>

            <div className="export-format-list">
              <button
                className={`export-format-btn ${selectedFormat === 'pdf' ? 'selected' : ''}`}
                onClick={() => handleExport('pdf')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap pdf">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">PDF Report</span>
                  <span className="export-format-desc">Presentation-ready report</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'excel' ? 'selected' : ''}`}
                onClick={() => handleExport('excel')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap excel">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">Excel .xlsx</span>
                  <span className="export-format-desc">Structured analytics workbook</span>
                </div>
              </button>

              <button
                className={`export-format-btn ${selectedFormat === 'csv' ? 'selected' : ''}`}
                onClick={() => handleExport('csv')}
                disabled={isExporting}
              >
                <div className="export-format-icon-wrap csv">
                  <FiFileText className="export-format-icon" />
                </div>
                <div className="export-format-info">
                  <span className="export-format-title">CSV .csv</span>
                  <span className="export-format-desc">Raw structured data</span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}

const ContentLibrary = ({ onEditDraft, onCreateNew }) => {
  const { getTranslations } = useLanguage();
  const t = getTranslations(false, true);
  const navigate = useNavigate();
  const [contentList, setContentList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("Approved");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [view, setView] = useState("library"); // 'library' | 'add-content'
  const [deletedItems, setDeletedItems] = useState(new Set()); // Track deleted items client-side
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentDeleteItem, setCurrentDeleteItem] = useState(null);
  const isFetchingRef = useRef(false); // Track if a fetch is in progress to prevent concurrent calls
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreContent, setHasMoreContent] = useState(true);
  const [lastDraftDoc, setLastDraftDoc] = useState(null);
  const [lastApprovalDoc, setLastApprovalDoc] = useState(null);
  const [pendingRevisions, setPendingRevisions] = useState(new Set()); // Track content IDs with pending revisions
  const [pendingRevisionsData, setPendingRevisionsData] = useState({}); // Store revision data with createdAt
  const [showDeleteRevisionModal, setShowDeleteRevisionModal] = useState(false);
  const [revisionToDelete, setRevisionToDelete] = useState(null);
  const [revisionDataToDelete, setRevisionDataToDelete] = useState(null);
  const [isDeletingRevision, setIsDeletingRevision] = useState(false);
  const [isDeletingContent, setIsDeletingContent] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [tooltip, setTooltip] = useState({ visible: false, text: '', x: 0, y: 0, color: '#1e293b' });

  // Tooltip handlers
  const handleActionMouseEnter = (e, text, color = '#1e293b') => {
    setTooltip({
      visible: true,
      text,
      x: e.clientX - 150,
      y: e.clientY,
      color
    });
  };

  const handleActionMouseMove = (e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({
        ...prev,
        x: e.clientX - 150,
        y: e.clientY
      }));
    }
  };

  const handleActionMouseLeave = () => {
    setTooltip({ visible: false, text: '', x: 0, y: 0 });
  };

  // Fetch content data and pending revisions in a single parallel round-trip.
  // This replaces the sequential drafts → approvals → revisions waterfall that
  // caused a guaranteed double loading-spinner on every page open.
  const fetchContent = useCallback(async (loadMore = false) => {
    if (isFetchingRef.current) return;

    try {
      isFetchingRef.current = true;
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setLastDraftDoc(null);
        setLastApprovalDoc(null);
        setHasMoreContent(true);
        setContentList([]);
      }

      const user = auth.currentUser;
      if (!user) {
        setContentList([]);
        setLoading(false);
        setLoadingMore(false);
        isFetchingRef.current = false;
        return;
      }

      // All three fetches run in parallel — no sequential waterfall
      let draftsResult, approvalsResult, revisions;
      try {
        [draftsResult, approvalsResult, revisions] = await Promise.all([
          draftsService.getPaginatedUserDrafts(100, loadMore ? lastDraftDoc : null),
          contentService.getPaginatedUserApprovals(user.uid, 100, loadMore ? lastApprovalDoc : null),
          revisionService.getAllRevisions()
        ]);
      } catch (parallelError) {
        console.error('ContentLibrary: Error in parallel fetch:', parallelError);
        // Individual fallbacks
        try { draftsResult = await draftsService.getPaginatedUserDrafts(100, loadMore ? lastDraftDoc : null); }
        catch { try { const d = await draftsService.getUserDrafts(); draftsResult = { data: d || [], lastDoc: null }; } catch { draftsResult = { data: [], lastDoc: null }; } }
        try { approvalsResult = await contentService.getPaginatedUserApprovals(user.uid, 100, loadMore ? lastApprovalDoc : null); }
        catch { try { const all = await approvalService.getAllApprovals(); approvalsResult = { data: all.filter(a => a.authorId === user.uid), lastDoc: null }; } catch { approvalsResult = { data: [], lastDoc: null }; } }
        revisions = [];
        console.warn('ContentLibrary: Revision fetch failed — pending revision badges will not be shown. Check Firestore indexes for the revisions collection.');
      }

      // Process revisions inline — no separate fetchPendingRevisions call needed
      if (Array.isArray(revisions)) {
        const pendingRevisionIds = new Set();
        const revisionsData = {};
        revisions
          .filter(rev => String(rev.status || "").toLowerCase() === "pending")
          .forEach(rev => {
            // Add both contentId and originalApprovalId for matching
            if (rev.contentId) {
              const contentId = String(rev.contentId);
              pendingRevisionIds.add(contentId);
              revisionsData[contentId] = { createdAt: rev.createdAt };
            }
            if (rev.originalApprovalId) {
              const approvalId = String(rev.originalApprovalId);
              pendingRevisionIds.add(approvalId);
              revisionsData[approvalId] = { createdAt: rev.createdAt };
            }
          });
        setPendingRevisions(pendingRevisionIds);
        setPendingRevisionsData(revisionsData);
      }

      setLastDraftDoc(draftsResult.lastDoc);
      setLastApprovalDoc(approvalsResult.lastDoc);

      let allContent = [
        ...(Array.isArray(draftsResult.data) ? draftsResult.data : []),
        ...(Array.isArray(approvalsResult.data) ? approvalsResult.data : [])
      ];

      // Filter out Community Outreach Programs
      allContent = allContent.filter(content => content.category !== "Community Outreach Programs");

      if (loadMore) {
        setContentList(prev => [...prev, ...allContent]);
        setHasMoreContent(allContent.length > 0);
      } else {
        setContentList(allContent);
        setHasMoreContent(allContent.length > 0);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetchingRef.current = false;
    }
  }, []);

  // Fetch pending revisions — inlined into fetchContent for parallel loading.
  // Kept as a standalone for use after delete operations.
  const fetchPendingRevisions = useCallback(async () => {
    try {
      const revisions = await revisionService.getAllRevisions();
      const pendingRevisionIds = new Set();
      const revisionsData = {};
      revisions
        .filter(rev => String(rev.status || "").toLowerCase() === "pending")
        .forEach(rev => {
          // Add both contentId and originalApprovalId for matching
          if (rev.contentId) {
            const contentId = String(rev.contentId);
            pendingRevisionIds.add(contentId);
            revisionsData[contentId] = { createdAt: rev.createdAt };
          }
          if (rev.originalApprovalId) {
            const approvalId = String(rev.originalApprovalId);
            pendingRevisionIds.add(approvalId);
            revisionsData[approvalId] = { createdAt: rev.createdAt };
          }
        });
      setPendingRevisions(pendingRevisionIds);
      setPendingRevisionsData(revisionsData);
    } catch (error) {
      console.error('Error fetching pending revisions:', error);
    }
  }, []);

  // Handle delete revision click
  const handleDeleteRevisionClick = async (content, e) => {
    if (e) {
      e.stopPropagation();
    }
    setRevisionToDelete(content);
    
    // Fetch the actual revision data from revisions collection
    try {
      const revisions = await revisionService.getAllRevisions();
      const pendingRevision = revisions.find(
        rev => rev.contentId === (content.contentId || content.id) && rev.status === "pending"
      );
      setRevisionDataToDelete(pendingRevision);
    } catch (error) {
      console.error('Error fetching revision data:', error);
    }
    
    setShowDeleteRevisionModal(true);
  };

  // Confirm delete revision
  const confirmDeleteRevision = async () => {
    if (!revisionToDelete) return;
    try {
      setIsDeletingRevision(true);
      // Find and delete the pending revision for this content
      const revisions = await revisionService.getAllRevisions();
      const pendingRevision = revisions.find(
        rev => rev.contentId === (revisionToDelete.contentId || revisionToDelete.id) && rev.status === "pending"
      );
      
      if (pendingRevision) {
        await revisionService.deleteRevision(pendingRevision.id);
        // Refresh pending revisions
        await fetchPendingRevisions();
      }
    } catch (error) {
      console.error('Error deleting revision:', error);
      alert('Failed to delete revision. Please try again.');
    } finally {
      setShowDeleteRevisionModal(false);
      setRevisionToDelete(null);
      setIsDeletingRevision(false);
    }
  };

  // Close delete revision modal
  const closeDeleteRevisionModal = () => {
    setShowDeleteRevisionModal(false);
    setRevisionToDelete(null);
    setRevisionDataToDelete(null);
  };

  const hasPendingRevision = (content) =>
    [content.contentId, content.id]
      .filter(Boolean)
      .some(id => pendingRevisions.has(String(id)));

  // Refetch content when statusFilter changes (skip on first render — auth effect handles mount)
  const isFirstRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstRenderRef.current) { isFirstRenderRef.current = false; return; }
    fetchContent();
  }, [statusFilter, fetchContent]);

  // Auth state listener — single source of truth for initial load
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        fetchContent();
      } else {
        setContentList([]);
        setLoading(false);
        isFetchingRef.current = false;
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Listen for draft saved events
  useEffect(() => {
    const handleDraftSaved = () => {
      fetchContent();
    };

    window.addEventListener('draftSaved', handleDraftSaved);
    
    return () => {
      window.removeEventListener('draftSaved', handleDraftSaved);
    };
  }, [fetchContent]);

  // Filter content based on search, category, and status
  const filteredContent = contentList.filter(content => {
    // Exclude client-side deleted content
    if (deletedItems.has(content.id)) {
      return false;
    }

    const matchesSearch = content.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         content.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === "" || content.category === categoryFilter;
    const matchesStatus = statusFilter === "all" ||
                          (statusFilter === "Approved" && content.status === "Approved") ||
                          (statusFilter === "Pending" && content.status === "Pending") ||
                          (statusFilter === "draft" && (content.status === "draft" || content.status === undefined)) ||
                          (statusFilter === "Rejected" && content.status === "Rejected");

    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Calculate status counts based on category filter
  const getStatusCounts = () => {
    const filteredByCategory = categoryFilter === ""
      ? contentList
      : contentList.filter(content => content.category === categoryFilter);

    return {
      approved: filteredByCategory.filter(c => c.status === "Approved").length,
      pending: filteredByCategory.filter(c => c.status === "Pending").length,
      draft: filteredByCategory.filter(c => c.status === "draft" || c.status === undefined).length,
      rejected: filteredByCategory.filter(c => c.status === "Rejected").length
    };
  };

  const statusCounts = getStatusCounts();

  // Sort filtered content
  const sortedContent = [...filteredContent].sort((a, b) => {
    // Sort based on status with appropriate date fields
    const getDateForSort = (item) => {
      const status = item.status || (item.status === undefined ? 'draft' : 'unknown');

      switch (status) {
        case 'draft':
          // Sort drafts by last modified (updatedAt)
          return item.updatedAt ? (item.updatedAt.toDate ? item.updatedAt.toDate() : new Date(item.updatedAt)) : new Date(0);
        case 'Pending':
          // Sort pending by submitted date (createdAt)
          return item.createdAt ? (item.createdAt.toDate ? item.createdAt.toDate() : new Date(item.createdAt)) : new Date(0);
        case 'Rejected':
          // Sort rejected by rejection date (rejectedAt)
          return item.rejectedAt ? (item.rejectedAt.toDate ? item.rejectedAt.toDate() : new Date(item.rejectedAt)) : new Date(0);
        case 'Approved':
          // Sort approved: prioritize pending revisions by revisedAt, then by approvedAt
          const contentId = item.contentId || item.id;
          if (hasPendingRevision(item) && pendingRevisionsData[contentId]?.createdAt) {
            return pendingRevisionsData[contentId].createdAt.toDate ?
              pendingRevisionsData[contentId].createdAt.toDate() :
              new Date(pendingRevisionsData[contentId].createdAt);
          }
          return item.approvedAt ? (item.approvedAt.toDate ? item.approvedAt.toDate() : new Date(item.approvedAt)) : new Date(0);
        default:
          return new Date(0);
      }
    };

    // For Approved status, also sort by whether item has pending revision (priority)
    if (statusFilter === 'Approved') {
      const hasPendingRevisionA = hasPendingRevision(a);
      const hasPendingRevisionB = hasPendingRevision(b);

      if (hasPendingRevisionA && !hasPendingRevisionB) return -1; // A has priority
      if (!hasPendingRevisionA && hasPendingRevisionB) return 1; // B has priority
    }

    const dateA = getDateForSort(a);
    const dateB = getDateForSort(b);
    return dateB - dateA; // Most recent first
  });

  // Calculate pagination values
  const totalPages = Math.ceil(sortedContent.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  
  // Show "Load More" indicator when there's more data on server
  const showLoadMore = hasMoreContent && currentPage >= totalPages - 1;

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    } else if (hasMoreContent && !loadingMore) {
      // Load more data when reaching the end
      fetchContent(true);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, categoryFilter, statusFilter, pendingRevisionsData]);

  // Format date (same as AdminPage to handle Firestore timestamps)
  const formatDate = (dateValue) => {
    if (!dateValue) return "N/A";
    
    // Handle Firestore timestamp objects
    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      // Firestore timestamp object
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      // Already a Date object
      date = dateValue;
    } else {
      // Try to parse as string
      date = new Date(dateValue);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    
    const dateStr = date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "2-digit", 
      year: "numeric" 
    });
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    return (
      <>
        {dateStr}<br />{timeStr}
      </>
    );
  };

  // Format date for modals (single line)
  const formatDateModal = (dateValue) => {
    if (!dateValue) return "N/A";
    
    let date;
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      date = dateValue.toDate();
    } else if (dateValue instanceof Date) {
      date = dateValue;
    } else {
      date = new Date(dateValue);
    }
    
    if (isNaN(date.getTime())) {
      return "Invalid Date";
    }
    
    const dateStr = date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "2-digit", 
      year: "numeric" 
    });
    const timeStr = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    
    return `${dateStr} ${timeStr}`;
  };

  // Handle actions based on status
  const handleAction = async (contentId, action, e) => {
    // Stop event propagation to prevent row click
    if (e) {
      e.stopPropagation();
    }
    
    try {
      switch (action) {
        case 'publish':
          await approvalService.updateApprovalStatus(contentId, 'Approved');
          break;
        case 'delete':
          // Client-side only deletion due to Firestore permissions
          setDeletedItems(prev => new Set([...prev, contentId]));
          break;
        case 'edit':
          // Navigate to edit page or open edit modal
          break;
        default:
          console.log('Unknown action:', action);
      }
      // Refresh content list
      const approvals = await approvalService.getAllApprovals();
      setContentList(approvals);
    } catch (error) {
      console.error('Error handling action:', error);
    }
  };

  const handleRowClick = (contentId) => {
    // Find the content to check its status
    const content = contentList.find(item => item.id === contentId);
    
    if (content && content.status === "draft") {
      // For drafts, call the onEditDraft callback
      if (onEditDraft) {
        onEditDraft(content);
      }
    } else {
      // For other statuses, navigate to article view page with from parameter
      window.location.href = `/article-view?id=${contentId}&from=library`;
    }
  };

  const handleDeleteClick = (content, e) => {
    if (e) {
      e.stopPropagation();
    }
    setCurrentDeleteItem(content);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!currentDeleteItem) return;
    try {
      setIsDeletingContent(true);
      if (currentDeleteItem.status === "draft" || !currentDeleteItem.status) {
        // Delete from drafts collection
        await draftsService.deleteDraft(currentDeleteItem.id);
      } else {
        // Delete from approvals collection
        await approvalService.deleteApproval(currentDeleteItem.id);
      }
      // Update local state
      setDeletedItems(prev => new Set([...prev, currentDeleteItem.id]));
      setShowDeleteModal(false);
      setCurrentDeleteItem(null);
    } catch (error) {
      console.error('Error deleting content:', error);
      alert('Failed to delete content. Please try again.');
    } finally {
      setIsDeletingContent(false);
    }
  };

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false);
    setCurrentDeleteItem(null);
  };

  // Export handlers
  const handleExport = async (format) => {
    setIsExporting(true);
    try {
      const reportDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      switch (format) {
        case 'pdf':
          await exportToPDF(reportDate);
          break;
        case 'excel':
          await exportToExcel(reportDate);
          break;
        case 'csv':
          await exportToCSV(reportDate);
          break;
        default:
          throw new Error('Invalid export format');
      }
      setShowExportModal(false);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportToPDF = async (reportDate) => {
    const pdf = createPDF({
      title: "Content Library Report"
    });

    // Separate data by status (case-insensitive)
    const draftItems = filteredContent.filter(item => !item.status || item.status.toLowerCase() === 'draft');
    const pendingItems = filteredContent.filter(item => item.status && item.status.toLowerCase() === 'pending');
    const rejectedItems = filteredContent.filter(item => item.status && item.status.toLowerCase() === 'rejected');
    const publishedItems = filteredContent.filter(item => item.status && item.status.toLowerCase() === 'approved');

    let startY = pdf.contentStartY;

    // Add draft items table if any
    if (draftItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('DRAFTS', pdf.margin, startY);
      startY += 10;
      
      const draftHeaders = ['Title', 'Category', 'Submitted Date', 'Last Modified'];
      const draftRows = draftItems.map(item => [
        item.title || 'N/A',
        item.category || 'N/A',
        item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'),
        item.updatedAt ? (item.updatedAt.toDate ? new Date(item.updatedAt.toDate()).toLocaleDateString() : new Date(item.updatedAt).toLocaleDateString()) : 'N/A'
      ]);

      pdf.autoTable({
        head: [draftHeaders],
        body: draftRows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
      startY = pdf.doc.lastAutoTable.finalY + 20;
    }

    // Add pending items table if any
    if (pendingItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('PENDING', pdf.margin, startY);
      startY += 10;
      
      const pendingHeaders = ['Title', 'Category', 'Submitted Date'];
      const pendingRows = pendingItems.map(item => [
        item.title || 'N/A',
        item.category || 'N/A',
        item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A')
      ]);

      pdf.autoTable({
        head: [pendingHeaders],
        body: pendingRows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
      startY = pdf.doc.lastAutoTable.finalY + 20;
    }

    // Add rejected items table if any
    if (rejectedItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('REJECTED', pdf.margin, startY);
      startY += 10;
      
      const rejectedHeaders = ['Title', 'Category', 'Submitted Date', 'Rejection Date', 'Reason for Rejection'];
      const rejectedRows = rejectedItems.map(item => [
        item.title || 'N/A',
        item.category || 'N/A',
        item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'),
        item.rejectedAt ? (item.rejectedAt.toDate ? new Date(item.rejectedAt.toDate()).toLocaleDateString() : new Date(item.rejectedAt).toLocaleDateString()) : 'N/A',
        item.rejectReason || 'No reason provided'
      ]);

      pdf.autoTable({
        head: [rejectedHeaders],
        body: rejectedRows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
      startY = pdf.doc.lastAutoTable.finalY + 20;
    }

    // Add published items table if any
    if (publishedItems.length > 0) {
      pdf.doc.setFontSize(14);
      pdf.doc.setFont('helvetica', 'bold');
      pdf.doc.text('PUBLISHED', pdf.margin, startY);
      startY += 10;
      
      const publishedHeaders = ['Title', 'Category', 'Submitted Date', 'Approved Date'];
      const publishedRows = publishedItems.map(item => [
        item.title || 'N/A',
        item.category || 'N/A',
        item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'),
        item.approvedAt ? (item.approvedAt.toDate ? new Date(item.approvedAt.toDate()).toLocaleDateString() : new Date(item.approvedAt).toLocaleDateString()) : 'N/A'
      ]);

      pdf.autoTable({
        head: [publishedHeaders],
        body: publishedRows,
        startY: startY,
        margin: { top: pdf.contentStartY, bottom: pdf.footerHeight + 10, left: pdf.margin, right: pdf.margin }
      });
    }

    pdf.save(`content-library-report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportToExcel = async (reportDate) => {
    const workbook = XLSX.utils.book_new();

    const summaryData = [
      ['CONTENT LIBRARY REPORT'],
      ['Report Date', reportDate],
      ['Status Filter', statusFilter],
      [],
      ['CONTENT SUMMARY'],
      ['Total Items', filteredContent.length],
      ['Draft', filteredContent.filter(item => !item.status || item.status.toLowerCase() === 'draft').length],
      ['Pending', filteredContent.filter(item => item.status && item.status.toLowerCase() === 'pending').length],
      ['Rejected', filteredContent.filter(item => item.status && item.status.toLowerCase() === 'rejected').length],
      ['Published', filteredContent.filter(item => item.status && item.status.toLowerCase() === 'approved').length],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

    // Content details - separate data by status (case-insensitive)
    const draftItems = filteredContent.filter(item => !item.status || item.status.toLowerCase() === 'draft');
    const pendingItems = filteredContent.filter(item => item.status && item.status.toLowerCase() === 'pending');
    const rejectedItems = filteredContent.filter(item => item.status && item.status.toLowerCase() === 'rejected');
    const publishedItems = filteredContent.filter(item => item.status && item.status.toLowerCase() === 'approved');

    const contentData = [
      ['CONTENT DETAILS'],
    ];

    // Add draft items
    if (draftItems.length > 0) {
      contentData.push(['DRAFTS']);
      contentData.push(['Title', 'Category', 'Submitted Date', 'Last Modified']);
      draftItems.forEach(item => {
        contentData.push([
          item.title || 'N/A',
          item.category || 'N/A',
          item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'),
          item.updatedAt ? (item.updatedAt.toDate ? new Date(item.updatedAt.toDate()).toLocaleDateString() : new Date(item.updatedAt).toLocaleDateString()) : 'N/A'
        ]);
      });
    }

    // Add pending items
    if (pendingItems.length > 0) {
      contentData.push([]);
      contentData.push(['PENDING']);
      contentData.push(['Title', 'Category', 'Submitted Date']);
      pendingItems.forEach(item => {
        contentData.push([
          item.title || 'N/A',
          item.category || 'N/A',
          item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A')
        ]);
      });
    }

    // Add rejected items
    if (rejectedItems.length > 0) {
      contentData.push([]);
      contentData.push(['REJECTED']);
      contentData.push(['Title', 'Category', 'Submitted Date', 'Rejection Date', 'Reason for Rejection']);
      rejectedItems.forEach(item => {
        contentData.push([
          item.title || 'N/A',
          item.category || 'N/A',
          item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'),
          item.rejectedAt ? (item.rejectedAt.toDate ? new Date(item.rejectedAt.toDate()).toLocaleDateString() : new Date(item.rejectedAt).toLocaleDateString()) : 'N/A',
          item.rejectReason || item.rejectionReason || item.reason || 'No reason provided'
        ]);
      });
    }

    // Add published items
    if (publishedItems.length > 0) {
      contentData.push([]);
      contentData.push(['PUBLISHED']);
      contentData.push(['Title', 'Category', 'Submitted Date', 'Approved Date']);
      publishedItems.forEach(item => {
        contentData.push([
          item.title || 'N/A',
          item.category || 'N/A',
          item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'),
          item.approvedAt ? (item.approvedAt.toDate ? new Date(item.approvedAt.toDate()).toLocaleDateString() : new Date(item.approvedAt).toLocaleDateString()) : 'N/A'
        ]);
      });
    }

    const contentSheet = XLSX.utils.aoa_to_sheet(contentData);
    XLSX.utils.book_append_sheet(workbook, contentSheet, 'Content');

    XLSX.writeFile(workbook, `content-library-report-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const exportToCSV = async (reportDate) => {
    let csvData = [
      ['CONTENT LIBRARY REPORT'],
      ['Report Date', reportDate],
      ['Status Filter', statusFilter],
      [],
      ['CONTENT SUMMARY'],
      ['Total Items', filteredContent.length],
      ['Draft', filteredContent.filter(item => !item.status || item.status.toLowerCase() === 'draft').length],
      ['Pending', filteredContent.filter(item => item.status && item.status.toLowerCase() === 'pending').length],
      ['Rejected', filteredContent.filter(item => item.status && item.status.toLowerCase() === 'rejected').length],
      ['Published', filteredContent.filter(item => item.status && item.status.toLowerCase() === 'approved').length],
      [],
      ['CONTENT DETAILS'],
    ];

    // Separate data by status (case-insensitive)
    const draftItems = filteredContent.filter(item => !item.status || item.status.toLowerCase() === 'draft');
    const pendingItems = filteredContent.filter(item => item.status && item.status.toLowerCase() === 'pending');
    const rejectedItems = filteredContent.filter(item => item.status && item.status.toLowerCase() === 'rejected');
    const publishedItems = filteredContent.filter(item => item.status && item.status.toLowerCase() === 'approved');

    // Add draft items
    if (draftItems.length > 0) {
      csvData.push(['DRAFTS']);
      csvData.push(['Title', 'Category', 'Submitted Date', 'Last Modified']);
      draftItems.forEach(item => {
        csvData.push([
          item.title || 'N/A',
          item.category || 'N/A',
          item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'),
          item.updatedAt ? (item.updatedAt.toDate ? new Date(item.updatedAt.toDate()).toLocaleDateString() : new Date(item.updatedAt).toLocaleDateString()) : 'N/A'
        ]);
      });
    }

    // Add pending items
    if (pendingItems.length > 0) {
      csvData.push([]);
      csvData.push(['PENDING']);
      csvData.push(['Title', 'Category', 'Submitted Date']);
      pendingItems.forEach(item => {
        csvData.push([
          item.title || 'N/A',
          item.category || 'N/A',
          item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A')
        ]);
      });
    }

    // Add rejected items
    if (rejectedItems.length > 0) {
      csvData.push([]);
      csvData.push(['REJECTED']);
      csvData.push(['Title', 'Category', 'Submitted Date', 'Rejection Date', 'Reason for Rejection']);
      rejectedItems.forEach(item => {
        csvData.push([
          item.title || 'N/A',
          item.category || 'N/A',
          item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'),
          item.rejectedAt ? (item.rejectedAt.toDate ? new Date(item.rejectedAt.toDate()).toLocaleDateString() : new Date(item.rejectedAt).toLocaleDateString()) : 'N/A',
          item.rejectReason || 'No reason provided'
        ]);
      });
    }

    // Add published items
    if (publishedItems.length > 0) {
      csvData.push([]);
      csvData.push(['PUBLISHED']);
      csvData.push(['Title', 'Category', 'Submitted Date', 'Approved Date']);
      publishedItems.forEach(item => {
        csvData.push([
          item.title || 'N/A',
          item.category || 'N/A',
          item.submittedAt ? (item.submittedAt.toDate ? new Date(item.submittedAt.toDate()).toLocaleDateString() : new Date(item.submittedAt).toLocaleDateString()) : (item.createdAt ? (item.createdAt.toDate ? new Date(item.createdAt.toDate()).toLocaleDateString() : new Date(item.createdAt).toLocaleDateString()) : 'N/A'),
          item.approvedAt ? (item.approvedAt.toDate ? new Date(item.approvedAt.toDate()).toLocaleDateString() : new Date(item.approvedAt).toLocaleDateString()) : 'N/A'
        ]);
      });
    }

    const csvContent = csvData.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `content-library-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  return (
    <>
      {view === "library" && (
        <div className="ma-main review-appointments-panel">
          {/* Content Library Header */}
          <div className="content-library-header-custom" style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)',
            borderRadius: '20px',
            padding: '32px',
            marginBottom: '24px',
            boxShadow: '0 8px 32px rgba(59, 130, 246, 0.3)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Content Curator - Left Aligned */}
            <div style={{
              position: 'absolute',
              left: '32px',
              top: '50%',
              transform: 'translateY(-50%)'
            }}>
              <div style={{
                padding: '16px 32px',
                borderRadius: '50px',
                background: 'rgba(147, 197, 253, 0.2)',
                border: '1px solid rgba(147, 197, 253, 0.3)'
              }}>
                <h2 style={{
                  margin: 0,
                  fontSize: '28px',
                  fontWeight: '700',
                  background: 'linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  color: 'transparent',
                  letterSpacing: '0.02em',
                  textShadow: 'none'
                }}>
                  Content Curator
                </h2>
              </div>
            </div>

            {/* Title and Subtitle */}
            <div style={{ textAlign: 'right' }}>
              <h2 style={{
                margin: 0,
                fontSize: '36px',
                fontWeight: '800',
                color: '#ffffff !important',
                background: 'none !important',
                WebkitBackgroundClip: 'unset !important',
                WebkitTextFillColor: '#ffffff !important',
                backgroundClip: 'unset !important',
                letterSpacing: '-0.02em',
                textShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                {t.contentLibrary}
              </h2>
              <p style={{
                margin: '12px 0 0 0',
                fontSize: '18px',
                color: '#ffffff',
                fontWeight: '500',
                textShadow: '0 1px 4px rgba(0,0,0,0.15)'
              }}>
                Manage and organize your educational content resources
              </p>
            </div>

            {/* CSS Animations */}
            <style>{`
              .content-library-header-custom h2 {
                color: #ffffff !important;
                background: none !important;
                -webkit-background-clip: unset !important;
                -webkit-text-fill-color: #ffffff !important;
                background-clip: unset !important;
              }
              .content-library-header-custom p {
                color: #ffffff !important;
              }
            `}</style>
          </div>
          
          {/* Top Row: Search, Category, Refresh, Export to PDF, Create New */}
          <div className="admin-aura-top-controls" style={{ marginTop: 20 }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <FiSearch style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#6b7280' }} />
              <input
                className="admin-aura-search"
                style={{ paddingLeft: 36, width: '100%' }}
                placeholder={t.search}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <select
              className="admin-aura-select"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">{t.allCategories}</option>
              {CONTENT_CATEGORIES.map(category => (
                <option key={category} value={category}>{category}</option>
              ))}
            </select>
            <button
              onClick={() => fetchContent()}
              disabled={loading}
              className="admin-primary-gradient-btn"
              style={{ opacity: loading ? 0.6 : 1, cursor: loading ? 'not-allowed' : 'pointer' }}
            >
              <FiRefreshCw /> Refresh
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="admin-primary-gradient-btn"
            >
              <FiDownload /> Export Report
            </button>
            <button
              onClick={onCreateNew}
              className="admin-primary-gradient-btn"
            >
              <FiPlus /> Create New
            </button>
          </div>

          {/* Second Row: Status Buttons + Pagination */}
          <div className="cl-top-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <div className="cl-status-buttons" style={{ display: "flex", gap: "8px" }}>
              <button 
                className={`cl-status-btn ${statusFilter === "Approved" ? "active" : ""}`}
                onClick={() => setStatusFilter("Approved")}
              >
                Published
                <span className="tab-count" style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "18px",
                  height: "18px",
                  padding: "0 5px",
                  background: statusFilter === "Approved" ? "rgba(255, 255, 255, 0.25)" : "#3b82f6",
                  borderRadius: "9px",
                  fontSize: "11px",
                  fontWeight: "600",
                  color: statusFilter === "Approved" ? "rgba(255, 255, 255, 0.9)" : "white",
                  marginLeft: "6px"
                }}>
                  {statusCounts.approved}
                </span>
              </button>
              <button
                className={`cl-status-btn ${statusFilter === "Pending" ? "active" : ""}`}
                onClick={() => setStatusFilter("Pending")}
              >
                Pending
                <span className="tab-count" style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "18px",
                  height: "18px",
                  padding: "0 5px",
                  background: statusFilter === "Pending" ? "rgba(255, 255, 255, 0.25)" : "#3b82f6",
                  borderRadius: "9px",
                  fontSize: "11px",
                  fontWeight: "600",
                  color: statusFilter === "Pending" ? "rgba(255, 255, 255, 0.9)" : "white",
                  marginLeft: "6px"
                }}>
                  {statusCounts.pending}
                </span>
              </button>
              <button 
                className={`cl-status-btn ${statusFilter === "draft" ? "active" : ""}`}
                onClick={() => setStatusFilter("draft")}
              >
                Draft
                <span className="tab-count" style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "18px",
                  height: "18px",
                  padding: "0 5px",
                  background: statusFilter === "draft" ? "rgba(255, 255, 255, 0.25)" : "#3b82f6",
                  borderRadius: "9px",
                  fontSize: "11px",
                  fontWeight: "600",
                  color: statusFilter === "draft" ? "rgba(255, 255, 255, 0.9)" : "white",
                  marginLeft: "6px"
                }}>
                  {statusCounts.draft}
                </span>
              </button>
              <button
                className={`cl-status-btn ${statusFilter === "Rejected" ? "active" : ""}`}
                onClick={() => setStatusFilter("Rejected")}
              >
                Rejected
                <span className="tab-count" style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "18px",
                  height: "18px",
                  padding: "0 5px",
                  background: statusFilter === "Rejected" ? "rgba(255, 255, 255, 0.25)" : "#3b82f6",
                  borderRadius: "9px",
                  fontSize: "11px",
                  fontWeight: "600",
                  color: statusFilter === "Rejected" ? "rgba(255, 255, 255, 0.9)" : "white",
                  marginLeft: "6px"
                }}>
                  {statusCounts.rejected}
                </span>
              </button>
            </div>
            
            {/* Pagination (matching ResourcesPanel style) */}
            <div style={{ 
              display: "flex", 
              gap: "16px", 
              alignItems: "center" 
            }}>
              <div style={{ fontSize: "14px", color: "#6b7280" }}>
                {sortedContent.length > 0 ? (
                  `${startIndex + 1}-${Math.min(endIndex, sortedContent.length)} of ${sortedContent.length}`
                ) : (
                  '0 of 0'
                )}
              </div>

              {/* Pagination Arrows */}
              <div style={{ display: "flex", gap: "8px" }}>
                <button 
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                  style={{ 
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white', 
                    border: 'none', 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '32px'
                  }}
                >
                  ←
                </button>
                
                <button 
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages || totalPages === 0 || loadingMore}
                  style={{ 
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'white', 
                    border: 'none', 
                    padding: '8px 12px', 
                    borderRadius: '4px', 
                    cursor: 'pointer',
                    width: '32px'
                  }}
                >
                  {loadingMore ? '...' : '→'}
                </button>
              </div>
              {loadingMore && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '8px', 
                  color: '#6b7280',
                  fontSize: '14px' 
                }}>
                  {t.loadingMore}
                </div>
              )}
            </div>
          </div>

          {/* Content Table */}
          <div className="admin-aura-table-wrapper" style={{ marginTop: 10 }}>
            <table className="admin-aura-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Category</th>
                  <th>{statusFilter === "draft" ? "Date Created" : "Submitted Date"}</th>
                  {statusFilter === "draft" && <th>Last Modified</th>}
                  {statusFilter === "Rejected" && <th>Rejection Date</th>}
                  {statusFilter === "Rejected" && <th>Reason for Rejection</th>}
                  {statusFilter !== "Pending" && statusFilter !== "Rejected" && statusFilter !== "draft" && <th>Approved Date</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <>
                    {[...Array(10)].map((_, i) => (
                      <tr key={i} className="admin-aura-skeleton-row">
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '40px', height: '40px', borderRadius: '4px' }} /></td>
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${55 + (i % 3) * 15}%` }} /></td>
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: `${45 + (i % 4) * 10}%` }} /></td>
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} /></td>
                        {statusFilter === "draft" && <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} /></td>}
                        {statusFilter === "Rejected" && <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} /></td>}
                        {statusFilter === "Rejected" && <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '60%' }} /></td>}
                        {statusFilter !== "Pending" && statusFilter !== "Rejected" && statusFilter !== "draft" && <td><div className="admin-aura-skeleton admin-aura-skeleton-text" style={{ width: '50%' }} /></td>}
                        <td><div className="admin-aura-skeleton admin-aura-skeleton-actions" /></td>
                      </tr>
                    ))}
                  </>
                ) : sortedContent.length === 0 ? (
                  <tr>
                    <td colSpan={
                      4 +
                      (statusFilter === "draft" ? 1 : 0) +
                      (statusFilter === "Rejected" ? 2 : 0) +
                      (statusFilter !== "Pending" && statusFilter !== "Rejected" && statusFilter !== "draft" ? 1 : 0)
                    } style={{ textAlign: "center", padding: "40px 20px", color: "#6b7280" }}>
                      No content found for the selected filter.
                    </td>
                  </tr>
                ) : (
                  sortedContent.slice(startIndex, endIndex).map((content) => (
                    <tr 
                      key={content.id} 
                      className="admin-aura-clickable-row"
                      onClick={() => handleRowClick(content.id)}
                    >
                      <td style={{ padding: "16px 10px 16px 20px" }}>
                        {content.socialImageUrl && content.socialImageUrl.startsWith('data:image') ? (
                          <img
                            src={content.socialImageUrl}
                            alt={content.title}
                            style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/40x40";
                            }}
                          />
                        ) : content.socialImageUrl && content.socialImageUrl.startsWith('http') ? (
                          <img
                            src={content.socialImageUrl}
                            alt={content.title}
                            style={{ width: "40px", height: "40px", objectFit: "cover", borderRadius: "4px" }}
                            onError={(e) => {
                              e.target.src = "https://via.placeholder.com/40x40";
                            }}
                          />
                        ) : (
                          <div style={{ width: "40px", height: "40px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", color: "#6b7280" }}>
                            N/A
                          </div>
                        )}
                      </td>
                      <td>{content.title || 'Untitled'}</td>
                      <td>{content.category || 'Uncategorized'}</td>
                      <td>{formatDate(content.submittedAt || content.createdAt)}</td>
                      {statusFilter === "draft" && <td>{formatDate(content.updatedAt || content.lastModified)}</td>}
                      {statusFilter === "Rejected" && <td>{formatDate(content.rejectedAt)}</td>}
                      {statusFilter === "Rejected" && <td style={{ maxWidth: "200px", wordWrap: "break-word", whiteSpace: "normal", textAlign: "left" }}>{content.rejectReason || 'No reason provided'}</td>}
                      {statusFilter !== "Pending" && statusFilter !== "Rejected" && statusFilter !== "draft" && <td>{formatDate(content.approvedAt)}</td>}
                      <td className="admin-aura-actions">
                        {(content.status === "Approved" || content.status === "Pending") && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {!hasPendingRevision(content) && (
                              <button
                                type="button"
                                className="ma-action-btn approve"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/curator-revise/${content.contentId || content.id}`);
                                }}
                                aria-label="Revise"
                                onMouseEnter={(e) => { e.target.style.background = '#dbeafe'; e.target.style.color = '#2563eb'; handleActionMouseEnter(e, "Revise Content", '#2563eb'); }}
                                onMouseMove={handleActionMouseMove}
                                onMouseLeave={(e) => { e.target.style.background = '#eff6ff'; e.target.style.color = '#2563eb'; handleActionMouseLeave(); }}
                                style={{ background: '#eff6ff', color: '#2563eb', borderColor: '#3b82f6', border: '1px solid', borderRadius: '12px' }}
                              >
                                <FiEdit size={16} />
                              </button>
                            )}
                            {hasPendingRevision(content) && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                <span style={{
                                  background: '#fff7ed',
                                  border: '1px solid #fdba74',
                                  color: '#c2410c',
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  whiteSpace: 'nowrap'
                                }}>
                                  Revision Pending
                                </span>
                                {pendingRevisionsData[content.contentId || content.id]?.createdAt && (
                                  <span
                                    style={{
                                      fontSize: '10px',
                                      color: '#c2410c',
                                      fontWeight: '500',
                                      opacity: 0.8
                                    }}
                                  >
                                    Submitted: {formatDateModal(pendingRevisionsData[content.contentId || content.id].createdAt)}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                        {(content.status === "draft" || !content.status || content.status === "Rejected") && (
                          <button
                            type="button"
                            className="ma-action-btn cancel"
                            onClick={(e) => handleDeleteClick(content, e)}
                            aria-label="Delete"
                            onMouseEnter={(e) => { e.target.style.background = '#fecaca'; e.target.style.color = '#dc2626'; handleActionMouseEnter(e, "Delete Content", '#dc2626'); }}
                            onMouseMove={handleActionMouseMove}
                            onMouseLeave={(e) => { e.target.style.background = '#fee2e2'; e.target.style.color = '#dc2626'; handleActionMouseLeave(); }}
                            style={{ background: '#fee2e2', color: '#dc2626', borderColor: '#ef4444', border: '1px solid', borderRadius: '12px' }}
                          >
                            <FiTrash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && currentDeleteItem && (
        <div className="modal-overlay" onClick={handleDeleteModalClose}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close modal-close-top-right" onClick={handleDeleteModalClose}>
              <FiX />
            </button>
            <div className="modal-header">
              <h2>Delete Content</h2>
            </div>
            <div className="modal-body">
              <div className="modal-icon" style={{ 
                width: "64px", 
                height: "64px", 
                borderRadius: "50%", 
                background: "#fee2e2", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                margin: "0 auto 16px"
              }}>
                <FiTrash2 size={32} color="#dc2626" />
              </div>
              <p className="modal-subtitle">Are you sure you want to delete this content? This action cannot be undone.</p>
              <div className="content-preview" style={{ 
                background: "#f9fafb", 
                padding: "16px", 
                borderRadius: "8px", 
                marginTop: "16px",
                border: "1px solid #e5e7eb"
              }}>
                <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>{currentDeleteItem.title}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                  <div style={{ fontSize: "14px", color: "#6b7280" }}>
                    <strong>Submitted Date:</strong> {formatDateModal(currentDeleteItem.submittedAt || currentDeleteItem.createdAt)}
                  </div>
                  <div style={{ fontSize: "14px", color: "#6b7280" }}>
                    <strong>Category:</strong> {currentDeleteItem.category || 'Uncategorized'}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="modal-btn modal-btn-cancel" 
                onClick={handleDeleteModalClose}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="modal-btn modal-btn-confirm"
                onClick={confirmDelete}
                disabled={isDeletingContent}
                style={{
                  backgroundColor: "#fee2e2",
                  borderColor: "#fee2e2",
                  color: "#dc2626",
                  transition: "all 0.2s ease",
                  opacity: isDeletingContent ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isDeletingContent) {
                    e.target.style.backgroundColor = "#dc2626";
                    e.target.style.borderColor = "#dc2626";
                    e.target.style.color = "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeletingContent) {
                    e.target.style.backgroundColor = "#fee2e2";
                    e.target.style.borderColor = "#fee2e2";
                    e.target.style.color = "#dc2626";
                  }
                }}
              >
                {isDeletingContent ? 'Deleting...' : <><FiTrash2 style={{ marginRight: "4px" }} /> Delete</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Revision Modal */}
      {showDeleteRevisionModal && revisionToDelete && (
        <div className="modal-overlay" onClick={closeDeleteRevisionModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close modal-close-top-right" onClick={closeDeleteRevisionModal}>
              <FiX />
            </button>
            <div className="modal-header">
              <h2>Delete Revision</h2>
            </div>
            <div className="modal-body">
              <div className="modal-icon" style={{ 
                width: "64px", 
                height: "64px", 
                borderRadius: "50%", 
                background: "#fee2e2", 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                margin: "0 auto 16px"
              }}>
                <FiTrash2 size={32} color="#dc2626" />
              </div>
              <p className="modal-subtitle">Are you sure you want to delete this revision? This action cannot be undone.</p>
              <div className="content-preview" style={{ 
                background: "#f9fafb", 
                padding: "16px", 
                borderRadius: "8px", 
                marginTop: "16px",
                border: "1px solid #e5e7eb"
              }}>
                <div style={{ fontWeight: "600", marginBottom: "12px", fontSize: "16px" }}>{revisionToDelete.title}</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "8px" }}>
                  <div style={{ fontSize: "14px", color: "#6b7280" }}>
                    <strong>Submitted Date:</strong> {formatDateModal(revisionDataToDelete?.createdAt || revisionToDelete.submittedAt || revisionToDelete.createdAt)}
                  </div>
                  <div style={{ fontSize: "14px", color: "#6b7280" }}>
                    <strong>Category:</strong> {revisionToDelete.category || 'Uncategorized'}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button 
                type="button" 
                className="modal-btn modal-btn-cancel" 
                onClick={closeDeleteRevisionModal}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="modal-btn modal-btn-confirm"
                onClick={confirmDeleteRevision}
                disabled={isDeletingRevision}
                style={{
                  backgroundColor: "#fee2e2",
                  borderColor: "#fee2e2",
                  color: "#dc2626",
                  transition: "all 0.2s ease",
                  opacity: isDeletingRevision ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isDeletingRevision) {
                    e.target.style.backgroundColor = "#dc2626";
                    e.target.style.borderColor = "#dc2626";
                    e.target.style.color = "white";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isDeletingRevision) {
                    e.target.style.backgroundColor = "#fee2e2";
                    e.target.style.borderColor = "#fee2e2";
                    e.target.style.color = "#dc2626";
                  }
                }}
              >
                {isDeletingRevision ? 'Deleting...' : <><FiTrash2 style={{ marginRight: "4px" }} /> Delete Revision</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        isExporting={isExporting}
        activeTab="content library"
      />

      {/* Tooltip */}
      {tooltip.visible && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x,
            top: tooltip.y,
            background: tooltip.color,
            color: '#ffffff',
            padding: '6px 12px',
            borderRadius: '6px',
            fontSize: '12px',
            fontWeight: '500',
            whiteSpace: 'nowrap',
            zIndex: 10000,
            pointerEvents: 'none',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
        >
          {tooltip.text}
        </div>
      )}

      {view === "add-content" && (
        <div className="cnp-container">
          <header className="cnp-header">
            <h2>Create New Content</h2>
            <button className="cc-btn-secondary" onClick={() => setView("library")}>
              Back to Library
            </button>
          </header>
          <ContentNewPanel />
        </div>
      )}
    </>
  );
};

export default ContentLibrary;
